import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import {
	KeyboardEvent,
	ReactElement,
	Ref,
	useContext,
	useCallback,
	CSSProperties
} from "react";
import { Checkbox } from "../Checkbox/Checkbox";
import { DropOverContext } from "../DropOver/DropOver";
import {
	FontIconName,
	IconValue,
	SvgIconValue
} from "../Icon/Icon";
import styles from "./ActionListItem.module.less";
import { useIndention } from "./ActionListItem";

export type ActionListCheckboxProps = {
	value: boolean;
	onChange(value: boolean): void;
	onKeyUp?: (args: KeyboardEvent<HTMLLIElement>) => void;
	onKeyDown?: (args: KeyboardEvent<HTMLLIElement>) => void;
	isDisabled?: boolean;
	text: string;
	trackingId?: string;
	shouldNotAutoCloseContainingDropOver?: boolean;
	icon?: FontIconName | SvgIconValue;
	tabIndex?: number;
	indentionLevel?: number;
	role?: "option";
	ref?: Ref<HTMLLIElement>;
};

export const ActionListCheckbox = (props: ActionListCheckboxProps): ReactElement => {
	const {
		value,
		onChange,
		onKeyUp,
		onKeyDown,
		isDisabled,
		text,
		trackingId,
		shouldNotAutoCloseContainingDropOver,
		icon,
		tabIndex,
		indentionLevel,
		role,
		ref
	} = props;

	const {
		onClose
	} = useContext(DropOverContext);

	const changeHandler = useCallback(
		(value: boolean): void => {
			if (!shouldNotAutoCloseContainingDropOver && onClose) {
				onClose();
			}
			onChange(value);
		},
		[
			onChange,
			onClose,
			shouldNotAutoCloseContainingDropOver
		]
	);

	const iconValue: IconValue | undefined = typeof icon === "string"
		? {
			kind: "font",
			color: "secondary",
			name: icon
		}
		: icon;

	const clickHandler = useCallback(
		(): void => {
			if (!isDisabled) {
				changeHandler(!value);
			}
		},
		[
			changeHandler,
			isDisabled,
			value
		]
	);

	const style: CSSProperties = useIndention(indentionLevel);

	return (
		<li
			{...setTrackingId(trackingId)}
			aria-selected={value}
			role={role}
			className={classNames(
				styles.root,
				{
					[styles.root_hoverable]: !isDisabled,
					[styles.root_disabled]: isDisabled,
				}
			)}
			style={style}
			onKeyUp={onKeyUp}
			onKeyDown={onKeyDown}
			tabIndex={tabIndex}
			ref={ref}
			onClick={clickHandler}
			title={text}
		>
			<div
				className={classNames(
					styles.contentHost,
					styles.contentHost_checkbox
				)}
			>
				<Checkbox
					value={value}
					onChange={changeHandler}
					isDisabled={isDisabled}
					text={text}
					icon={iconValue}
					color="primary"
					shouldBubbleClick={false}
				/>
			</div>
		</li>
	);
};
ActionListCheckbox.displayName = "ActionListCheckbox";
